import request from '#/utils/request';
import { keysToCamel } from '#/api/adapters/wireCase';
import { parseVer4DetailResponse } from '#/api/adapters/envelope';
import {
  serializeRepairRequestAddLine,
  serializeRepairRequestRemoveLine,
  serializeRepairRequestSerialCorrection,
} from './correctionSerialize';

/**
 * 成立後更正（be doc f77b08e9 #85–#88）。報修單須 IN_PROGRESS；每支都要 reason＋最新 updatedAt。
 * 增減品項 response 多帶 `completion_confirmation_required`。
 */
function parseWithCompletionFlag(raw) {
  return {
    ...parseVer4DetailResponse(raw),
    completionConfirmationRequired: Boolean(raw?.completion_confirmation_required),
  };
}

/**
 * PATCH `/aftersales/repair-request/{id}/corrections`
 * @param {string} id
 * @param {object} body 已 snake 的 body（見 serializeRepairRequestCorrection）
 */
export async function correctRepairRequest(id, body) {
  const { data: raw } = await request.patch(`/aftersales/repair-request/${id}/corrections`, body);
  return parseVer4DetailResponse(raw);
}

/**
 * GET `/aftersales/repair-request/{id}/corrections`
 * → `{ data: [{ id, actorId, reason, changedAt, before, after }] }`
 */
export async function fetchRepairRequestCorrections(id) {
  const { data: raw } = await request.get(`/aftersales/repair-request/${id}/corrections`);
  return {
    message: raw?.message,
    route: raw?.route,
    data: keysToCamel(raw?.list ?? []),
  };
}

/** PATCH `/aftersales/repair-request/{id}/lines/{lineId}/serial`；有維修草稿須先取消。 */
export async function correctRepairRequestLineSerial(id, lineId, payload) {
  const { data: raw } = await request.patch(
    `/aftersales/repair-request/${id}/lines/${lineId}/serial`,
    serializeRepairRequestSerialCorrection(payload),
  );
  return parseVer4DetailResponse(raw);
}

/** POST `/aftersales/repair-request/{id}/lines` */
export async function addRepairRequestLine(id, payload) {
  const { data: raw } = await request.post(
    `/aftersales/repair-request/${id}/lines`,
    serializeRepairRequestAddLine(payload),
  );
  return parseWithCompletionFlag(raw);
}

/** POST `/aftersales/repair-request/{id}/lines/{lineId}/remove`；不可移除最後一筆。 */
export async function removeRepairRequestLine(id, lineId, payload) {
  const { data: raw } = await request.post(
    `/aftersales/repair-request/${id}/lines/${lineId}/remove`,
    serializeRepairRequestRemoveLine(payload),
  );
  return parseWithCompletionFlag(raw);
}

/** GET `/aftersales/repair-request/{id}/completion-after-removal` → `{ completionConfirmationRequired }` */
export async function fetchRepairRequestCompletionAfterRemoval(id) {
  const { data: raw } = await request.get(
    `/aftersales/repair-request/${id}/completion-after-removal`,
  );
  return {
    message: raw?.message,
    completionConfirmationRequired: Boolean(raw?.completion_confirmation_required),
  };
}

/** POST `/aftersales/repair-request/{id}/completion-after-removal`；body 僅 `updated_at`。 */
export async function completeRepairRequestAfterRemoval(id, { updatedAt }) {
  const { data: raw } = await request.post(
    `/aftersales/repair-request/${id}/completion-after-removal`,
    { updated_at: updatedAt ?? null },
  );
  return parseWithCompletionFlag(raw);
}
